import { Controller } from "@hotwired/stimulus";
import { showToast } from "../../../application/toolkit/toast_component/show_toast";

const OPEN_EVENT = "votes:dashboard:add-to-vote-box";

interface SelectedRow {
  id?: number | string;
  name?: string;
}

interface BoxRow {
  id: number;
  title: string;
  elections_count: number;
  organization: { id: number; name: string };
}

interface ListResponse {
  vote_boxes?: BoxRow[];
  page?: number;
  has_next?: boolean;
}

// The grid api this dialog needs: after a write the rows it just moved are stale, and the selection
// they came from is meaningless.
interface DashboardGridApi {
  refreshServerSide(params?: { purge?: boolean }): void;
  deselectAll(): void;
}

export default class extends Controller {
  static targets = [
    "overlay",
    "subtitle",
    "message",
    "search",
    "searchInput",
    "list",
    "pagination",
    "prevButton",
    "nextButton",
    "pageLabel",
    "submitButton",
  ];

  static values = { voteBoxesUrl: String, currentVoteBoxId: Number, actionLabel: String, newVoteBoxUrl: String };

  declare readonly overlayTarget: HTMLElement;
  declare readonly subtitleTarget: HTMLElement;
  declare readonly messageTarget: HTMLElement;
  declare readonly searchTarget: HTMLElement;
  declare readonly searchInputTarget: HTMLInputElement;
  declare readonly listTarget: HTMLElement;
  declare readonly paginationTarget: HTMLElement;
  declare readonly prevButtonTarget: HTMLButtonElement;
  declare readonly nextButtonTarget: HTMLButtonElement;
  declare readonly pageLabelTarget: HTMLElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly newVoteBoxUrlValue: string;
  declare readonly voteBoxesUrlValue: string;
  // 0 when the dialog is not hosted by a vote box dashboard — Stimulus Number values have no
  // "absent" state.
  declare readonly currentVoteBoxIdValue: number;
  declare readonly actionLabelValue: string;

  private openHandler: ((event: Event) => void) | null = null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;
  private overlayClickHandler: ((event: MouseEvent) => void) | null = null;
  private gridReadyHandler: ((event: Event) => void) | null = null;

  private gridApi: DashboardGridApi | null = null;
  private electionIds: string[] = [];
  private page = 1;
  private hasNext = false;
  private selectedBoxId: number | null = null;
  private submitting = false;
  private search = "";
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  connect() {
    this.openHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ ids?: (string | number)[]; rows?: SelectedRow[] }>).detail;
      this.open(detail?.rows ?? [], detail?.ids ?? []);
    };
    document.addEventListener(OPEN_EVENT, this.openHandler);

    this.gridReadyHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ gridApi: DashboardGridApi | null }>).detail;
      this.gridApi = detail?.gridApi ?? null;
    };
    document.addEventListener("votes:dashboard:grid-ready", this.gridReadyHandler);
    document.dispatchEvent(new CustomEvent("votes:dashboard:request-grid-api"));

    this.keydownHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !this.overlayTarget.hidden) this.close();
    };
    document.addEventListener("keydown", this.keydownHandler);

    this.overlayClickHandler = (event: MouseEvent) => {
      if (event.target === this.overlayTarget) this.close();
    };
    this.overlayTarget.addEventListener("click", this.overlayClickHandler);
  }

  disconnect() {
    if (this.openHandler) {
      document.removeEventListener(OPEN_EVENT, this.openHandler);
      this.openHandler = null;
    }
    if (this.gridReadyHandler) {
      document.removeEventListener("votes:dashboard:grid-ready", this.gridReadyHandler);
      this.gridReadyHandler = null;
    }
    if (this.keydownHandler) {
      document.removeEventListener("keydown", this.keydownHandler);
      this.keydownHandler = null;
    }
    if (this.overlayClickHandler) {
      this.overlayTarget.removeEventListener("click", this.overlayClickHandler);
      this.overlayClickHandler = null;
    }
  }

  private open(rows: SelectedRow[], ids: (string | number)[]) {
    this.electionIds = ids.map((id) => String(id));
    this.selectedBoxId = null;
    this.page = 1;
    this.submitting = false;
    this.submitButtonTarget.disabled = true;
    this.listTarget.innerHTML = "";
    this.hideMessage();
    this.paginationTarget.hidden = true;
    this.clearSearch();
    this.searchTarget.hidden = true;

    this.subtitleTarget.textContent = this.describeSelection(rows);

    this.overlayTarget.hidden = false;

    // Shown before the rows arrive so the dialog does not jump, and focused so a customer with many
    // boxes can type straight away.
    this.searchTarget.hidden = false;
    this.searchInputTarget.focus();

    void this.loadPage(1);
  }

  // The grid selection is what opened this dialog, so closing it — cancel, Escape, the overlay, or a
  // finished attach — ends the selection too. Leaving the rows ticked behind a closed dialog left
  // the toolbar armed for a selection the customer had already walked away from.
  close() {
    this.overlayTarget.hidden = true;
    this.listTarget.innerHTML = "";
    this.selectedBoxId = null;
    this.submitButtonTarget.disabled = true;
    this.clearSearch();
    this.gridApi?.deselectAll();
  }

  // Debounced so a full round-trip fires once per pause in typing, not once per keystroke — the
  // same 375 ms the grid's own search settles on.
  onSearchInput() {
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      const term = this.searchInputTarget.value.trim();
      if (term === this.search) return;

      this.search = term;
      void this.loadPage(1);
    }, 375);
  }

  private clearSearch() {
    if (this.searchTimer !== null) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    this.search = "";
    this.searchInputTarget.value = "";
  }

  prevPage() {
    if (this.page <= 1) return;
    void this.loadPage(this.page - 1);
  }

  nextPage() {
    if (!this.hasNext) return;
    void this.loadPage(this.page + 1);
  }

  private describeSelection(rows: SelectedRow[]): string {
    const count = this.electionIds.length;
    if (count === 1) return rows[0]?.name ? String(rows[0].name) : "1 election selected";

    return `${count} elections selected`;
  }

  private async loadPage(page: number) {
    this.renderLoading();

    try {
      const response = await fetch(this.listUrl(page), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("Could not load vote boxes.");

      const data = (await response.json()) as ListResponse;
      this.page = data.page ?? page;
      this.hasNext = Boolean(data.has_next);

      this.renderList(data.vote_boxes ?? []);
    } catch {
      this.listTarget.innerHTML = "";
      this.showMessage("Could not load vote boxes. Close this and try again.", true);
    }
  }

  private listUrl(page: number): string {
    // active_only: a box the attach would reject is no destination, so it is never listed.
    const params = new URLSearchParams({ page: String(page), active_only: "true" });
    if (this.search !== "") params.set("search", this.search);
    // Opened from inside a box: that box is where the rows already are, so it is not a destination.
    if (this.currentVoteBoxIdValue > 0) params.set("exclude_vote_box_id", String(this.currentVoteBoxIdValue));

    return `${this.voteBoxesUrlValue}?${params.toString()}`;
  }

  private renderLoading() {
    this.hideMessage();
    this.listTarget.innerHTML = "";
    const loading = document.createElement("p");
    loading.className = this.bem("loading");
    loading.textContent = "Loading vote boxes…";
    this.listTarget.appendChild(loading);
  }

  private renderList(boxes: BoxRow[]) {
    this.listTarget.innerHTML = "";
    this.selectedBoxId = null;
    this.submitButtonTarget.disabled = true;

    if (boxes.length === 0) {
      this.paginationTarget.hidden = true;
      // The search box stays up on an empty result — it is the only way back to the full list.
      this.searchTarget.hidden = this.search === "";
      const empty = this.emptyState();
      this.showMessage(empty.message, false, empty.createUrl);
      return;
    }

    this.searchTarget.hidden = false;
    this.hideMessage();
    boxes.forEach((box) => this.listTarget.appendChild(this.buildRow(box)));
    this.renderPagination();
  }

  // True when the dialog is hosted by a vote box dashboard: the rows are already in a box, so every
  // destination on offer is a different one and the copy says "move" throughout.
  private get isMove(): boolean {
    return this.currentVoteBoxIdValue > 0;
  }

  // A blank createUrl means the host cannot create boxes, so the link is dropped rather than 404ing.
  private emptyState(): { message: string; createUrl?: string } {
    const qualifier = this.isMove ? "other active vote box" : "active vote box";
    if (this.search !== "") return { message: `No ${qualifier} matches “${this.search}”.` };
    if (this.page > 1) return { message: "No more vote boxes on this page." };
    if (!this.newVoteBoxUrlValue) return { message: `No ${qualifier} available.` };

    return { message: `No ${qualifier} available.`, createUrl: this.newVoteBoxUrlValue };
  }

  // Title + id, one line each. The election count and organization line said nothing about whether a
  // box is the right destination, and it doubled the height of every row; the two fields kept are the
  // two the search matches, so what identifies a box in the list is what the customer can type.
  private buildRow(box: BoxRow): HTMLElement {
    const row = document.createElement("label");
    row.className = this.bem("row");

    const radio = document.createElement("input");
    radio.type = "radio";
    radio.name = "vote_box_id";
    radio.value = String(box.id);
    radio.className = this.bem("row-radio");
    radio.addEventListener("change", () => this.selectBox(box.id, row));

    const title = document.createElement("span");
    title.className = this.bem("row-title");
    title.textContent = box.title;

    const id = document.createElement("span");
    id.className = this.bem("row-id");
    id.textContent = `ID ${box.id}`;

    row.appendChild(radio);
    row.appendChild(title);
    row.appendChild(id);
    return row;
  }

  private countLabel(count: number): string {
    return count === 1 ? "1 election" : `${count} elections`;
  }

  private selectBox(boxId: number, row: HTMLElement) {
    this.selectedBoxId = boxId;
    this.submitButtonTarget.disabled = false;

    const selectedClass = `${this.bem("row")}--selected`;
    this.listTarget.querySelectorAll(`.${this.bem("row")}`).forEach((el) => el.classList.remove(selectedClass));
    row.classList.add(selectedClass);
  }

  private renderPagination() {
    const paginated = this.page > 1 || this.hasNext;
    this.paginationTarget.hidden = !paginated;
    if (!paginated) return;

    this.prevButtonTarget.disabled = this.page <= 1;
    this.nextButtonTarget.disabled = !this.hasNext;
    this.pageLabelTarget.textContent = `Page ${this.page}`;
  }

  async submit() {
    if (this.selectedBoxId === null || this.submitting || this.electionIds.length === 0) return;

    this.submitting = true;
    this.submitButtonTarget.disabled = true;
    this.hideMessage();

    const boxId = this.selectedBoxId;
    const count = this.electionIds.length;

    try {
      const response = await fetch(`${this.voteBoxesUrlValue}/${boxId}/elections`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ election_ids: this.electionIds }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { errors?: string[] };
        const fallback = this.isMove
          ? "Could not move to this vote box. It must be an active vote box."
          : "Could not add to this vote box. It must be an active vote box.";
        throw new Error(data.errors?.join(", ") || fallback);
      }

      this.close();
      document.dispatchEvent(new CustomEvent("votes:dashboard:vote-boxes-changed"));

      // Only the server-side grid announces its api (it owns the drop zones); a grid that never did
      // has no way to re-read the moved rows, so fall back to a reload rather than leaving stale ones.
      if (!this.gridApi) {
        window.location.reload();
        return;
      }

      // close() already dropped the selection; the rows themselves are stale after the move.
      showToast(`${this.countLabel(count)} ${this.isMove ? "moved" : "added"} to the vote box.`, "success");
      this.gridApi.refreshServerSide({ purge: true });
    } catch (error) {
      this.showMessage((error as Error).message, true);
      this.submitButtonTarget.disabled = false;
    } finally {
      this.submitting = false;
    }
  }

  private showMessage(message: string, isError: boolean, createUrl?: string) {
    this.messageTarget.textContent = message;
    this.messageTarget.classList.toggle(`${this.bem("message")}--empty`, !isError);
    this.messageTarget.hidden = false;
    if (!createUrl) return;

    const link = document.createElement("a");
    link.className = this.bem("message-link");
    link.href = createUrl;
    link.textContent = "New Vote Box";
    this.messageTarget.appendChild(document.createTextNode(" "));
    this.messageTarget.appendChild(link);
  }

  private hideMessage() {
    this.messageTarget.hidden = true;
    this.messageTarget.textContent = "";
  }

  private bem(element: string): string {
    return `votes--dashboard--add-to-vote-box-modal-component__${element}`;
  }

  private csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
  }
}
